import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { feedbackService } from '../services/feedback.service';

const Feedback: React.FC = () => {
  const [feedback, setFeedback] = useState({
    name: '',
    email: '',
    rating: 0,
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFeedback((prev) => ({
      ...prev,
      [name]: name === 'rating' ? parseInt(value, 10) : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitting(true);

    feedbackService.submitFeedback({
      name: feedback.name.trim() || undefined,
      email: feedback.email.trim() || undefined,
      rating: feedback.rating,
      message: feedback.message.trim(),
    })
      .then(() => {
        setSubmitted(true);
        setTimeout(() => setSubmitted(false), 5000);
        setFeedback({
          name: '',
          email: '',
          rating: 0,
          message: '',
        });
      })
      .catch((error: any) => {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Unable to submit feedback right now.';
        setSubmitError(typeof message === 'string' ? message : 'Unable to submit feedback right now.');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const feedbackPillars = [
    { title: 'Product quality', text: 'Tell us what feels polished and what still feels rough.' },
    { title: 'Local discovery', text: 'Share how the nearby-teacher experience is working for you.' },
    { title: 'Trust and clarity', text: 'Point out anything confusing in payment, sessions, or workflow.' },
  ];

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.10),_transparent_25%),radial-gradient(circle_at_bottom_right,_rgba(249,115,22,0.10),_transparent_20%),linear-gradient(180deg,_#f8fafc_0%,_#ecfeff_48%,_#eef2ff_100%)]">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <section className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_520px]">
          <div className="overflow-hidden rounded-[32px] border border-white/70 bg-[linear-gradient(135deg,_rgba(15,23,42,0.96)_0%,_rgba(30,41,59,0.94)_38%,_rgba(8,145,178,0.90)_100%)] px-5 py-8 text-white shadow-[0_30px_100px_rgba(15,23,42,0.16)] sm:px-8 sm:py-10 lg:px-10">
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-cyan-100">
              Product Feedback
            </span>
            <h1 className="mt-5 max-w-3xl text-4xl font-semibold tracking-tight sm:text-5xl">
              Help shape the next version of LocalAlly.
            </h1>
            <p className="mt-5 max-w-2xl text-base text-slate-200 sm:text-lg">
              Strong feedback helps improve nearby teacher discovery, classroom flow, payments, and the overall user experience.
            </p>

            <div className="mt-8 grid gap-4">
              {feedbackPillars.map((item) => (
                <div key={item.title} className="rounded-[24px] border border-white/12 bg-white/10 p-4 backdrop-blur">
                  <h2 className="text-lg font-semibold text-white">{item.title}</h2>
                  <p className="mt-2 text-sm leading-6 text-slate-200">{item.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-[24px] border border-white/12 bg-white/10 p-4 backdrop-blur">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-100">Best feedback is specific</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {['What worked', 'What felt unclear', 'What should improve first'].map((item) => (
                  <span key={item} className="rounded-full border border-white/12 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.14em] text-white">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-[32px] border border-white/70 bg-white/85 p-5 shadow-[0_24px_70px_rgba(15,23,42,0.08)] backdrop-blur sm:p-8">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Share feedback</p>
                <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-900">
                  Tell us what to improve
                </h2>
                <p className="mt-3 text-sm text-slate-600">
                  Your input can be short and direct. Honest detail is more useful than long generic praise.
                </p>
              </div>
              <div className="rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs font-semibold text-cyan-700">
                User-driven
              </div>
            </div>

            <div className="mt-8">
              {submitted ? (
                <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Feedback received</p>
                      <p className="mt-1 text-sm text-emerald-700">
                        Thank you for taking the time to share your thoughts. It helps us prioritize real improvements.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="name" className="mb-2 block text-sm font-medium text-slate-700">
                      Your name <span className="text-slate-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      id="name"
                      value={feedback.name}
                      onChange={handleChange}
                      className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="mb-2 block text-sm font-medium text-slate-700">
                      Email <span className="text-slate-400">(optional)</span>
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={feedback.email}
                      onChange={handleChange}
                      className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500"
                      placeholder="you@example.com"
                    />
                    <p className="mt-2 text-xs text-slate-500">
                      Add email only if you want a response from the team.
                    </p>
                  </div>

                  <fieldset>
                    <legend className="mb-3 text-sm font-medium text-slate-700">
                      How would you rate your experience?
                    </legend>
                    <div className="grid gap-3 sm:grid-cols-5">
                      {[5, 4, 3, 2, 1].map((rating) => {
                        const isSelected = feedback.rating === rating;
                        return (
                          <label
                            key={rating}
                            htmlFor={`rating-${rating}`}
                            className={`cursor-pointer rounded-2xl border px-4 py-3 text-center text-sm font-medium transition ${
                              isSelected
                                ? 'border-slate-950 bg-slate-950 text-white'
                                : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                            }`}
                          >
                            <input
                              id={`rating-${rating}`}
                              name="rating"
                              type="radio"
                              required
                              checked={feedback.rating === rating}
                              onChange={handleChange}
                              value={rating}
                              className="sr-only"
                            />
                            {rating} {rating === 1 ? 'star' : 'stars'}
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>

                  <div>
                    <label htmlFor="message" className="mb-2 block text-sm font-medium text-slate-700">
                      Your feedback
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={6}
                      required
                      value={feedback.message}
                      onChange={handleChange}
                      className="block w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500"
                      placeholder="What do you like, what feels weak, and what should improve first?"
                    />
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
                    Strong feedback usually includes one thing that worked, one thing that did not, and one suggestion.
                  </div>

                  {submitError ? (
                    <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">
                      {submitError}
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex justify-center rounded-2xl bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-cyan-600 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2"
                    >
                      {submitting ? 'Submitting...' : 'Submit feedback'}
                    </button>
                    <p className="text-xs text-slate-500">
                      Need help instead?{' '}
                      <Link to="/contact" className="font-medium text-cyan-700 hover:text-cyan-800">
                        Contact our support team
                      </Link>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Feedback;
